import React, { Fragment, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import Loader from "../components/Loader";
import { WEBSERVICE, SITENAMEALIAS, isTestMode } from "../utils/init";
import { getAuthToken, getAuthUuid, getFeesByCurrencyId } from "../utils/service";
import { showToast, showHttpError } from "../utils/library";

// ─────────────────────────────────────────────────────────────────
// CSS
// ─────────────────────────────────────────────────────────────────
const CSS = `
.ecfc-page { padding: 32px 24px 80px; max-width: 100%; overflow-x: hidden; }
.ecfc-hdr { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.ecfc-hdr-left { display: flex; flex-direction: column; gap: 4px; }
.ecfc-title { font-size: 22px; font-weight: 700; letter-spacing: -.4px; color: var(--text-color); margin: 0; }
.ecfc-sub { font-size: 13px; color: var(--text-color); opacity: .65; margin: 0; }

/* ── Balance section ── */
.ecfc-balance-section { margin-bottom: 24px; }
.ecfc-balance-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
.ecfc-balance-card { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 12px; padding: 20px 22px; display: flex; flex-direction: column; gap: 6px; position: relative; overflow: hidden; }
.ecfc-balance-card::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: var(--btn-color); border-radius: 12px 12px 0 0; }
.ecfc-balance-card-icon { width: 38px; height: 38px; border-radius: 10px; background: color-mix(in srgb, var(--btn-color) 12%, transparent); display: flex; align-items: center; justify-content: center; color: var(--btn-color); font-size: 16px; margin-bottom: 6px; }
.ecfc-balance-card-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .7px; color: var(--text-color); opacity: .55; }
.ecfc-balance-card-value { font-size: 26px; font-weight: 700; color: var(--btn-color); letter-spacing: -.5px; line-height: 1.1; }
.ecfc-balance-card-sub { font-size: 12px; color: var(--text-color); opacity: .5; margin-top: 2px; }

/* ── Section heading ── */
.ecfc-section-hdr { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; flex-wrap: wrap; gap: 10px; }
.ecfc-section-title { font-size: 13px; font-weight: 700; color: var(--text-color); opacity: .7; display: flex; align-items: center; gap: 7px; margin: 0; }
.ecfc-count-badge { display: inline-flex; align-items: center; justify-content: center; min-width: 22px; height: 18px; padding: 0 6px; background: color-mix(in srgb, var(--btn-color) 14%, transparent); color: var(--btn-color); font-size: 11px; font-weight: 700; border-radius: 10px; }

/* ── Table ── */
.ecfc-tbl-wrap { background: var(--body-color-less); border: 1px solid var(--body-color-less2); border-radius: 12px; overflow-x: auto; -webkit-overflow-scrolling: touch; }
.ecfc-tbl-wrap table { width: 100%; border-collapse: collapse; min-width: 600px; }
.ecfc-tbl-wrap thead tr { border-bottom: 1px solid var(--body-color-less2); }
.ecfc-tbl-wrap thead th { padding: 12px 16px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .65px; color: var(--text-color); opacity: .6; text-align: left; white-space: nowrap; }
.ecfc-tbl-wrap tbody tr { border-bottom: 1px solid var(--body-color-less2); transition: background .15s; cursor: pointer; }
.ecfc-tbl-wrap tbody tr:last-child { border-bottom: none; }
.ecfc-tbl-wrap tbody tr:hover { background: color-mix(in srgb, var(--btn-color) 4%, transparent); }
.ecfc-tbl-wrap tbody td { padding: 13px 16px; font-size: 13px; vertical-align: middle; }
.ecfc-td-contractno { font-weight: 700; color: var(--btn-color); font-family: monospace; font-size: 13px; }
.ecfc-td-amount { font-weight: 700; color: var(--text-color); font-size: 14px; }
.ecfc-td-date { color: var(--text-color); opacity: .75; }
.ecfc-td-sub { font-size: 11px; color: var(--text-color); opacity: .5; margin-top: 2px; }

/* ── Withdraw button ── */
.ecfc-withdraw-btn { display: inline-flex; align-items: center; gap: 6px; padding: 5px 14px; border-radius: 7px; border: 1px solid color-mix(in srgb, var(--btn-color) 40%, transparent); background: color-mix(in srgb, var(--btn-color) 10%, transparent); color: var(--btn-color); font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .15s; white-space: nowrap; }
.ecfc-withdraw-btn:hover { background: color-mix(in srgb, var(--btn-color) 20%, transparent); }

/* ── Status badges ── */
.ecfc-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; white-space: nowrap; }
.ecfc-badge::before { content: ""; width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; background: currentColor; }
.ecfc-badge.funded          { background: rgba(16,185,129,.13);  color: #10b981; }
.ecfc-badge.fund-initiated  { background: rgba(245,158,11,.12);  color: #f59e0b; }
.ecfc-badge.created         { background: rgba(37,99,235,.12);   color: #60a5fa; }
.ecfc-badge.pending         { background: rgba(245,158,11,.12);  color: #f59e0b; }
.ecfc-badge.completed       { background: rgba(16,185,129,.13);  color: #10b981; }
.ecfc-badge.cancelled       { background: rgba(239,68,68,.10);   color: #f87171; }
.ecfc-badge.disputed        { background: rgba(139,92,246,.12);  color: #a78bfa; }
.ecfc-badge.released        { background: rgba(16,185,129,.13);  color: #10b981; }
.ecfc-badge.default         { background: rgba(100,116,139,.12); color: var(--text-color); opacity: .7; }

/* ── Empty / Error ── */
.ecfc-empty { display: flex; flex-direction: column; align-items: center; padding: 60px 20px; gap: 14px; text-align: center; }
.ecfc-empty-icon { font-size: 36px; opacity: .25; color: var(--text-color); }
.ecfc-empty-text { font-size: 14px; color: var(--text-color); opacity: .55; }
.ecfc-error { display: flex; flex-direction: column; align-items: center; padding: 40px 20px; gap: 10px; text-align: center; }
.ecfc-error-icon { font-size: 28px; color: #f87171; opacity: .7; }
.ecfc-error-text { font-size: 13px; color: #f87171; }
.ecfc-retry-btn { margin-top: 4px; height: 32px; padding: 0 16px; border-radius: 7px; border: 1px solid rgba(239,68,68,.3); background: rgba(239,68,68,.08); color: #f87171; font-family: inherit; font-size: 12px; cursor: pointer; transition: all .15s; }
.ecfc-retry-btn:hover { background: rgba(239,68,68,.16); }

/* ── Skeleton ── */
.ecfc-skeleton-row td { padding: 13px 16px; }
.ecfc-skeleton-line { height: 12px; border-radius: 6px; background: linear-gradient(90deg, var(--body-color-less2) 25%, color-mix(in srgb, var(--body-color-less2) 60%, transparent) 50%, var(--body-color-less2) 75%); background-size: 200% 100%; animation: ecfc-shimmer 1.4s infinite; }
.ecfc-skeleton-line.short  { width: 55%; }
.ecfc-skeleton-line.xshort { width: 38%; }
@keyframes ecfc-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

@keyframes ecfc-fade-up { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: translateY(0); } }
.ecfc-view-in { animation: ecfc-fade-up .2s ease; }

@media (max-width: 700px) {
  .ecfc-page { padding: 16px 10px 80px; }
  .ecfc-hdr { flex-direction: column; }
  .ecfc-balance-grid { grid-template-columns: 1fr 1fr; }
}
@media (max-width: 480px) {
  .ecfc-balance-grid { grid-template-columns: 1fr; }
}
`;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  FUNDED:         { label: "Funded",         cls: "funded"         },
  FUND_INITIATED: { label: "Fund Initiated", cls: "fund-initiated" },
  CREATED:        { label: "Created",        cls: "created"        },
  PENDING:        { label: "Pending",        cls: "pending"        },
  COMPLETED:      { label: "Completed",      cls: "completed"      },
  CANCELLED:      { label: "Cancelled",      cls: "cancelled"      },
  DISPUTED:       { label: "Disputed",       cls: "disputed"       },
  DISPUTE:        { label: "Disputed",       cls: "disputed"       },
  RELEASED:       { label: "Released",       cls: "released"       },
};

function StatusBadge({ status }) {
  const s = STATUS_MAP[(status || "").toUpperCase()] || { label: status || "—", cls: "default" };
  return <span className={`ecfc-badge ${s.cls}`}>{s.label}</span>;
}

function fmtDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

function fmtAmt(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function getHomeCurrencyId() {
  try {
    const assets = JSON.parse(localStorage.getItem(SITENAMEALIAS + "_assets") || "[]");
    const hc = localStorage.getItem(SITENAMEALIAS + "_home_currency") || "";
    const found = assets.find((a) => (a.assetCode || "").toUpperCase() === hc.toUpperCase());
    return found?.assetId || null;
  } catch {
    return null;
  }
}

// ─────────────────────────────────────────────────────────────────
// Modal inline styles (mirrors Dashboard's styles object)
// ─────────────────────────────────────────────────────────────────
const ms = {
  overlay: { padding: "28px 24px", background: "var(--body-color-less)", borderRadius: 16, color: "var(--text-color)" },
  header: { display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 20 },
  icon: { width: 40, height: 40, borderRadius: 10, background: "color-mix(in srgb,#10b981 14%,transparent)", display: "flex", alignItems: "center", justifyContent: "center", color: "#10b981", fontSize: 18, flexShrink: 0 },
  title: { fontSize: 16, fontWeight: 700, color: "var(--text-color)", margin: 0 },
  subtitle: { fontSize: 12, opacity: 0.5, marginTop: 2 },
  contractNo: { fontSize: 11, marginTop: 4, color: "#10b981", fontWeight: 600 },
  label: { display: "block", fontSize: 12, fontWeight: 600, opacity: 0.65, marginBottom: 6, color: "var(--text-color)" },
  input: { width: "100%", height: 40, borderRadius: 8, border: "1px solid var(--body-color-less2)", background: "var(--body-color-less)", color: "var(--text-color)", padding: "0 12px", fontSize: 14, outline: "none", fontFamily: "inherit" },
  hint: { fontSize: 12, opacity: 0.5, marginTop: 6 },
  errText: { color: "red", fontSize: 12, marginTop: 4 },
  termsRow: { display: "flex", alignItems: "flex-start", gap: 10, margin: "18px 0 24px" },
  termsLabel: { fontSize: 12.5, color: "var(--text-color)", opacity: 0.8, lineHeight: "1.55", cursor: "pointer" },
  termsLink: { color: "#10b981", textDecoration: "underline" },
  footer: { display: "flex", gap: 10, justifyContent: "center", marginTop: 4 },
  btnConfirm: { height: 38, padding: "0 22px", borderRadius: 8, border: "none", background: "#10b981", color: "#fff", fontFamily: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity .15s" },
  btnCancel: { height: 38, padding: "0 18px", borderRadius: 8, border: "1px solid var(--body-color-less2)", background: "transparent", color: "var(--text-color)", fontFamily: "inherit", fontSize: 13, cursor: "pointer" },
};

// ─────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────
export default function EscrowFundedContracts() {
  const history = useHistory();
  const homeCurrency = localStorage.getItem(SITENAMEALIAS + "_home_currency") || "USD";

  // ── Data state ────────────────────────────────────────────────
  const [balance, setBalance]       = useState(null);
  const [contracts, setContracts]   = useState([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Withdraw modal state ──────────────────────────────────────
  const [showLoader, setShowLoader]               = useState(false);
  const [withdrawStep, setWithdrawStep]           = useState(null); // null | 'brand'
  const [withdrawTerms, setWithdrawTerms]         = useState(false);
  const [withdrawContractNo, setWithdrawContractNo] = useState("");
  const [withdrawBalance, setWithdrawBalance]     = useState(null);
  const [withdrawCurrency, setWithdrawCurrency]   = useState(homeCurrency);
  const [withdrawAmount, setWithdrawAmount]       = useState("");
  const [withdrawAmountError, setWithdrawAmountError] = useState("");

  // ── Fetch balance + contracts ─────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const merchantId = parseInt(localStorage.getItem(SITENAMEALIAS + "_merchant_id"), 10);

    setLoading(true);
    setError(null);

    fetch(`${WEBSERVICE}/escrow/get-funded-contracts-balance?merchantId=${merchantId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: "bearer " + getAuthToken(),
        UUID: getAuthUuid(),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.status === false) {
          setBalance(null);
          setContracts([]);
          setLoading(false);
          return;
        }
        const { contracts: list = [], ...balanceSummary } = data.data || {};
        setBalance(balanceSummary);
        setContracts(Array.isArray(list) ? list : []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Failed to load contracts. Please try again.");
        setBalance(null);
        setContracts([]);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [refreshKey]);

  // ── Withdraw helpers ──────────────────────────────────────────
  const closeWithdrawModal = () => {
    setWithdrawStep(null);
    setWithdrawTerms(false);
    setWithdrawAmount("");
    setWithdrawAmountError("");
  };

  const openWithdrawModal = (contract) => {
    const currency = contract.fundedCurrency || homeCurrency;
    setWithdrawBalance(contract.refundedAmount);
    setWithdrawCurrency(currency);
    setWithdrawContractNo(contract.contractNo);
    setWithdrawTerms(false);
    setWithdrawAmount("");
    setWithdrawAmountError("");

    const homeCurrencyId = getHomeCurrencyId();
    if (homeCurrencyId) {
      getFeesByCurrencyId({
        currencyId: homeCurrencyId,
        merchant_id: localStorage.getItem(SITENAMEALIAS + "_merchant_id"),
      })
        .then(() => setWithdrawStep("brand"))
        .catch(() => setWithdrawStep("brand"));
    } else {
      setWithdrawStep("brand");
    }
  };

  const fetchExchangeUserAndStore = async () => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    const brokerId   = localStorage.getItem(SITENAMEALIAS + "_brokerId");

    const loginRes = await fetch(`${WEBSERVICE}/merchant/auto-login/exchange`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + getAuthToken(),
        UUID: getAuthUuid(),
      },
      body: JSON.stringify({ merchantId, brokerId, uuid: getAuthUuid() }),
    });
    const loginData = await loginRes.json();
    if (loginData.error?.error_data != "0") {
      throw new Error(loginData.error?.error_message || "Exchange login failed");
    }
    const { token, uuid, userId } = loginData;
    localStorage.setItem("paybito_access_token", token);

    const userRes = await fetch("https://accounts.paybito.com/api/user/GetUserDetails", {
      method: "POST",
      headers: { "Content-Type": "application/json", authorization: "BEARER " + token },
      body: JSON.stringify({ uuid, userId }),
    });
    const userData = await userRes.json();
    const details  = userData.userResult;
    localStorage.setItem(SITENAMEALIAS + "_paybito_id",   details.userId);
    localStorage.setItem(SITENAMEALIAS + "_paybito_uuid", details.uuid);
  };

  const transferEscrowToBrandWallet = () => {
    const merchantId = localStorage.getItem(SITENAMEALIAS + "_merchant_id");
    const customerId = localStorage.getItem(SITENAMEALIAS + "_paybito_id");
    setShowLoader(true);
    fetch(`${WEBSERVICE}/escrow/transfer-escrow-to-wallet`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "bearer " + getAuthToken(),
        UUID: getAuthUuid(),
      },
      body: JSON.stringify({
        contractNo: withdrawContractNo,
        customerId,
        merchantId,
        amount: parseFloat(withdrawAmount),
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        setShowLoader(false);
        if (data?.returnId !== 1) {
          showToast("error", data?.message || "Transfer failed");
          return;
        }
        showToast("success", data?.message || "Transfer successful");
        closeWithdrawModal();
        setRefreshKey((k) => k + 1);
      })
      .catch((err) => {
        setShowLoader(false);
        showHttpError(err);
      });
  };

  const handleWithdrawConfirm = async () => {
    if (isTestMode) {
      showToast("error", "This feature is not available for test mode");
      return;
    }
    if (Number(withdrawAmount) <= 0) {
      showToast("error", "Amount must be greater than 0");
      return;
    }
    if (Number(withdrawAmount) > Number(withdrawBalance)) {
      showToast("error", "Amount must be less than or equal to contract amount");
      return;
    }

    const existingPaybitoId = localStorage.getItem(SITENAMEALIAS + "_paybito_id");
    if (!existingPaybitoId) {
      setShowLoader(true);
      try {
        await fetchExchangeUserAndStore();
      } catch (err) {
        setShowLoader(false);
        showToast("error", err.message || "Failed to fetch exchange user details. Please try again.");
        return;
      }
    }

    setWithdrawStep(null);
    transferEscrowToBrandWallet();
  };

  const COL_SPAN = 8;
  const exchangeName = localStorage.getItem(SITENAMEALIAS + "_exchange") || "Exchange";
  const canConfirm = withdrawTerms && withdrawAmount && !withdrawAmountError;

  // ────────────────────────────────────────────────────────────
  return (
    <Fragment>
      <style>{CSS}</style>
      <div className="container-scroller">
        <Sidebar />
        <Header />
        <div className="main-panel">
          <div className="ecfc-page ecfc-view-in">

            {/* ── Page header ── */}
            <div className="ecfc-hdr">
              <div className="ecfc-hdr-left">
                <h1 className="ecfc-title">
                  <i className="fas fa-coins" style={{ marginRight: 10, opacity: .8 }} />
                  Funded Contracts
                </h1>
                <p className="ecfc-sub">Overview of your escrow balance and all funded contracts</p>
              </div>
            </div>

            {/* ── Escrow Balance section ── */}
            <div className="ecfc-balance-section">
              <div className="ecfc-section-hdr">
                <p className="ecfc-section-title">
                  <i className="fas fa-wallet" style={{ fontSize: 13, color: "var(--btn-color)" }} />
                  Escrow Balance
                </p>
              </div>
              <div className="ecfc-balance-grid">

                {/* Total Funded */}
                <div className="ecfc-balance-card">
                  <div className="ecfc-balance-card-icon">
                    <i className="fas fa-dollar-sign" />
                  </div>
                  <div className="ecfc-balance-card-label">Total Funded</div>
                  <div className="ecfc-balance-card-value">
                    {loading
                      ? <div className="ecfc-skeleton-line" style={{ width: "80%", height: 28, borderRadius: 6 }} />
                      : `${homeCurrency} ${fmtAmt(balance?.totalFunded ?? 0)}`}
                  </div>
                  <div className="ecfc-balance-card-sub">{homeCurrency} across all contracts</div>
                </div>

                {/* Released */}
                <div className="ecfc-balance-card">
                  <div className="ecfc-balance-card-icon">
                    <i className="fas fa-check-circle" />
                  </div>
                  <div className="ecfc-balance-card-label">Released</div>
                  <div className="ecfc-balance-card-value">
                    {loading
                      ? <div className="ecfc-skeleton-line" style={{ width: "80%", height: 28, borderRadius: 6 }} />
                      : `${homeCurrency} ${fmtAmt(balance?.totalReleased ?? 0)}`}
                  </div>
                  <div className="ecfc-balance-card-sub">Paid out to payees</div>
                </div>

                {/* In Escrow */}
                <div className="ecfc-balance-card">
                  <div className="ecfc-balance-card-icon">
                    <i className="fas fa-lock" />
                  </div>
                  <div className="ecfc-balance-card-label">In Escrow</div>
                  <div className="ecfc-balance-card-value">
                    {loading
                      ? <div className="ecfc-skeleton-line" style={{ width: "80%", height: 28, borderRadius: 6 }} />
                      : `${homeCurrency} ${fmtAmt(balance?.inEscrow ?? 0)}`}
                  </div>
                  <div className="ecfc-balance-card-sub">Currently held</div>
                </div>

                {/* Active Contracts */}
                <div className="ecfc-balance-card">
                  <div className="ecfc-balance-card-icon">
                    <i className="fas fa-file-contract" />
                  </div>
                  <div className="ecfc-balance-card-label">Active Contracts</div>
                  <div className="ecfc-balance-card-value">
                    {loading
                      ? <div className="ecfc-skeleton-line" style={{ width: "50%", height: 28, borderRadius: 6 }} />
                      : (balance?.activeContracts ?? 0)}
                  </div>
                  <div className="ecfc-balance-card-sub">Funded &amp; in progress</div>
                </div>

              </div>
            </div>

            {/* ── Contracts table section ── */}
            <div>
              <div className="ecfc-section-hdr">
                <p className="ecfc-section-title">
                  <i className="fas fa-list-alt" style={{ fontSize: 13, color: "var(--btn-color)" }} />
                  All Funded Contracts
                  {contracts.length > 0 && !loading && (
                    <span className="ecfc-count-badge">{contracts.length}</span>
                  )}
                </p>
              </div>

              {/* Table */}
              <div className="ecfc-tbl-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Contract No</th>
                      <th>Amount</th>
                      <th>Funded Amount</th>
                      <th>Created At</th>
                      <th>Contract Status</th>
                      <th>Funding Status</th>
                      <th>Refunded Amount</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr className="ecfc-skeleton-row" key={i}>
                          <td><div className="ecfc-skeleton-line" style={{ width: "60%" }} /></td>
                          <td><div className="ecfc-skeleton-line short" /></td>
                          <td><div className="ecfc-skeleton-line short" /></td>
                          <td><div className="ecfc-skeleton-line xshort" /></td>
                          <td><div className="ecfc-skeleton-line" style={{ width: "50%" }} /></td>
                          <td><div className="ecfc-skeleton-line" style={{ width: "50%" }} /></td>
                          <td><div className="ecfc-skeleton-line short" /></td>
                          <td><div className="ecfc-skeleton-line xshort" /></td>
                        </tr>
                      ))
                    ) : error ? (
                      <tr>
                        <td colSpan={COL_SPAN}>
                          <div className="ecfc-error">
                            <div className="ecfc-error-icon"><i className="fas fa-exclamation-circle" /></div>
                            <p className="ecfc-error-text">{error}</p>
                            <button
                              className="ecfc-retry-btn"
                              onClick={() => setRefreshKey((k) => k + 1)}
                            >
                              Try Again
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : contracts.length === 0 ? (
                      <tr>
                        <td colSpan={COL_SPAN}>
                          <div className="ecfc-empty">
                            <div className="ecfc-empty-icon">
                              <i className="fas fa-coins" />
                            </div>
                            <p className="ecfc-empty-text">No funded contracts found.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      contracts.map((c, i) => (
                        <tr key={c.contractNo || i}>
                          <td>
                            <div className="ecfc-td-contractno">{c.contractNo}</div>
                            <div className="ecfc-td-sub">{c.contractTitle}</div>
                          </td>
                          <td>
                            <div className="ecfc-td-amount">{fmtAmt(c.amount)}{c.fundedCurrency ? ` ${c.fundedCurrency}` : ""}</div>
                          </td>
                          <td>
                            <div className="ecfc-td-amount">{fmtAmt(c.fundedAmount || 0)}{c.fundedCurrency ? ` ${c.fundedCurrency}` : ""}</div>
                          </td>
                          <td className="ecfc-td-date">{fmtDate(c.createdAt)}</td>
                          <td><StatusBadge status={c.contractStatus} /></td>
                          <td>
                            {c.fundingStatus
                              ? <StatusBadge status={c.fundingStatus} />
                              : <span style={{ opacity: 0.35 }}>—</span>}
                          </td>
                          <td>
                            {c.refundedAmount !== null && c.refundedAmount !== undefined && c.refundedAmount !== ""
                              ? <div className="ecfc-td-amount">{fmtAmt(c.refundedAmount)}{c.fundedCurrency ? ` ${c.fundedCurrency}` : ""}</div>
                              : <span style={{ opacity: 0.35 }}>—</span>}
                          </td>
                          <td>
                            {(() => {
                              const cs = (c.contractStatus || "").toUpperCase();
                              const refunded = c.isRefunded === 1;
                              const showWithdraw =
                                cs === "RELEASED" ||
                                cs === "RELEASE_APPROVED" ||
                                (cs === "DISPUTED" && refunded) ||
                                (cs === "REFUNDED" && refunded);
                              return showWithdraw ? (
                                <button
                                  className="ecfc-withdraw-btn"
                                  onClick={(e) => { e.stopPropagation(); openWithdrawModal(c); }}
                                >
                                  <i className="fas fa-wallet" />
                                  Withdraw
                                </button>
                              ) : <span style={{ opacity: 0.35 }}>—</span>;
                            })()}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
          <Footer />
        </div>
      </div>

      {/* ── Withdraw to Exchange Wallet Modal ── */}
      <Modal show={withdrawStep === "brand"} backdrop="static" keyboard={false} centered>
        <div style={ms.overlay}>
          <div style={ms.header}>
            <div style={ms.icon}>
              <i className="fa fa-wallet" />
            </div>
            <div>
              <div style={ms.title}>Withdraw to {exchangeName} Wallet</div>
              <div style={ms.subtitle}>Funds will go to your {withdrawCurrency} wallet in exchange</div>
              {withdrawContractNo && (
                <div style={ms.contractNo}>Contract: {withdrawContractNo}</div>
              )}
            </div>
          </div>

          {/* Amount */}
          <div style={{ marginBottom: 4 }}>
            <label style={ms.label}>Amount</label>
            <input
              type="number"
              min={0}
              step="any"
              style={ms.input}
              placeholder="Enter amount"
              value={withdrawAmount}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "") {
                  setWithdrawAmount("");
                  setWithdrawAmountError("");
                  return;
                }
                const num = parseFloat(val);
                const avail = parseFloat(withdrawBalance);
                let err = "";
                if (!isNaN(num) && num <= 0) {
                  err = "Amount must be greater than 0";
                } else if (!isNaN(num) && !isNaN(avail) && num > avail) {
                  err = `Amount must be less than or equal to refunded amount (${withdrawBalance} ${withdrawCurrency})`;
                }
                setWithdrawAmount(val);
                setWithdrawAmountError(err);
              }}
            />
            {withdrawAmountError && <p style={ms.errText}>{withdrawAmountError}</p>}
            <div style={ms.hint}>
              Refunded Amount: <span style={{ fontWeight: 700 }}>{withdrawBalance} {withdrawCurrency}</span>
            </div>
          </div>

          {/* Terms */}
          <div style={ms.termsRow}>
            <input
              type="checkbox"
              id="ecfc-withdraw-terms"
              checked={withdrawTerms}
              onChange={(e) => setWithdrawTerms(e.target.checked)}
              style={{ marginTop: 2, accentColor: "#10b981", width: 15, height: 15, flexShrink: 0, cursor: "pointer" }}
            />
            <label htmlFor="ecfc-withdraw-terms" style={ms.termsLabel}>
              I agree to the{" "}
              <a href="https://www.paybito.com/escrow/payer-terms" target="_blank" rel="noopener noreferrer" style={ms.termsLink}>Payer Terms</a>
              ,{" "}
              <a href="https://www.paybito.com/merchants-using-pay-services-terms-of-use/" target="_blank" rel="noopener noreferrer" style={ms.termsLink}>Merchant Terms</a>
              {" "}and{" "}
              <a href="https://www.paybito.com/exchange-fees/" target="_blank" rel="noopener noreferrer" style={ms.termsLink}>Fee Schedule</a>
              .
            </label>
          </div>

          {/* Buttons */}
          <div style={ms.footer}>
            <button
              style={{ ...ms.btnConfirm, opacity: canConfirm ? 1 : 0.45 }}
              disabled={!canConfirm}
              onClick={handleWithdrawConfirm}
            >
              Confirm Withdrawal
            </button>
            <button style={ms.btnCancel} onClick={closeWithdrawModal}>
              Cancel
            </button>
          </div>
        </div>
      </Modal>

      <Loader show={showLoader} />
    </Fragment>
  );
}
